import { map, first } from 'rxjs/operators';
import { HttpClient } from '@angular/common/http';
import { OrderDocument } from '../../../../shared/objects/order/orderDocument';
import { AuthService } from '../../../../core/services/auth.service';
import { Observable, of, Subject } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import { Injectable } from '@angular/core';
import {
  OrderDocumentType,
  OrderDocumentTypeExternal,
} from '../../../../shared/objects/order/orderDocumentType';
import { CmrStatus } from '../../../../shared/objects/enum/cmrStatus';
import { DocumentStatus } from '../../../../shared/objects/enum/documentStatus';
import { Order } from '../../../../shared/objects/order/order';

import { DocumentService } from '../../home/services/document.service';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Constants } from '../../../../shared/const/constants';
import { CommonUtils } from '../../../../shared/utils/common.utils';

@Injectable()
export class OrderDocumentService {
  cmrStatus = CmrStatus;
  allDocumentTypes: OrderDocumentType[];
  allDocumentTypesExternal: OrderDocumentTypeExternal[];
  cmrExternalNameForBusinessUnit: OrderDocumentTypeExternal;

  private allDocumentTypesReady: Subject<boolean> = new Subject<boolean>();
  public allDocumentTypesReady$ = this.allDocumentTypesReady.asObservable();
  constructor(
    private httpClient: HttpClient,
    private authService: AuthService,
    private docService: DocumentService,
    private notificationService: ToastrService,
    private translateService: TranslateService,
  ) {
    this.getOrderDocumentTypeExternalName()
      .toPromise()
      .then(res => {
        this.allDocumentTypesExternal = res;
      })
      .then(() => {
        this.getDocumentTypes()
          .pipe(
            first(),
            map(types => {
              return types.map(docType => {
                return {
                  ...docType,
                  externalNameForbusinessUnit:
                    this.resolveOrderDocumentTypeExternalName(docType),
                };
              });
            }),
          )
          .toPromise()
          .then(types => {
            this.allDocumentTypes = types;
            this.updateAllDocumentsReady(true);
          });
      });
  }

  private updateAllDocumentsReady(val: boolean) {
    this.allDocumentTypesReady.next(val);
  }
  getDocumentTypes(): Observable<OrderDocumentType[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<OrderDocumentType[]>(
      `${environment.orderDocumentServiceUrl}/getTypes`,
      { headers },
    );
  }

  getDocumentsBy(orderId: number): Observable<OrderDocument[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<OrderDocument[]>(
      `${environment.orderDocumentServiceUrl}/${orderId}`,
      { headers },
    );
  }

  getDocumentBy(documentId: number): Observable<OrderDocument> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<OrderDocument>(
      `${environment.orderDocumentServiceUrl}/getDocument/${documentId}`,
      { headers },
    );
  }

  private getFileBy(documentId: number, extension: string): Observable<Blob> {
    const headers = this.authService.getBearerRequestHeaders();
    const docType = this.docService.getDocumentTypeBy(extension);
    return this.httpClient
      .get<Blob>(`${environment.fileServiceUrl}/getOrderFile/${documentId}`, {
        headers: headers,
        responseType: 'blob' as 'json',
      })
      .pipe(
        map(res => {
          return new Blob([res], { type: docType });
        }),
      );
  }

  private upload(document: OrderDocument): Observable<OrderDocument> {
    const headers = this.authService.getBearerRequestHeaders();

    const data = new FormData();
    data.set('document.id', document.id.toString());
    data.set('document.status', document.status.toString());
    data.set('document.description', document.description.toString());
    data.set('document.fileName', document.fileName.toString());
    data.set('document.type', document.type.toString());
    data.set(
      'document.orderDocumentTypeId',
      document.orderDocumentTypeId.toString(),
    );
    data.set('document.orderId', document.orderId.toString());
    data.set('file', document.file);

    return this.httpClient.put<OrderDocument>(
      `${environment.fileServiceUrl}/uploadOrderDocument/${document.orderId}`,
      data,
      { headers },
    );
  }

  getRequiredDocumentTypes(orderId): Observable<number[]> {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<number[]>(
      `${environment.orderDocumentServiceUrl}/getRequiredDocumentTypes/${orderId}`,
      { headers },
    );
  }

  getOrderDocumentStatus(order: Order): DocumentStatus {
    let documentStatus = DocumentStatus.NotRequired;

    if (!this.allDocumentTypes) {
      return documentStatus;
    }

    const atLeastOneDocumentExists =
      !!order.orderDocuments && order.orderDocuments.length > 0;
    const otherDocType = this.allDocumentTypes.find(
      type => type.code === Constants.OTHER_DOCUMENT_IDENTIFIER,
    );
    const nrOfDocumentTypes = this.allDocumentTypes.filter(
      type => type.code !== Constants.OTHER_DOCUMENT_IDENTIFIER,
    ).length;

    if (
      order.orderDocuments.length === 0 &&
      order.cmrStatus !== this.cmrStatus.Missing &&
      order.cmrStatus !== this.cmrStatus.Valid &&
      !order.missingRequiredDocuments
    ) {
      documentStatus = DocumentStatus.NotRequired;
    } else if (
      order.cmrStatus === this.cmrStatus.Missing ||
      order.missingRequiredDocuments
    ) {
      documentStatus = DocumentStatus.Missing;
    } else if (atLeastOneDocumentExists && !order.missingRequiredDocuments) {
      documentStatus = DocumentStatus.Valid;
    }

    return documentStatus;
  }

  uploadFile(
    orderId: number,
    doc: OrderDocument,
    file: File,
  ): Observable<OrderDocument> {
    let documentToBeUploaded = doc;
    documentToBeUploaded = {
      ...documentToBeUploaded,
      file: file,
      orderId: orderId,
      fileName: file.name,
      status: DocumentStatus.Valid,
      description: 'Uploaded via Carrier Portal',
    };

    return this.upload(documentToBeUploaded);
  }

  viewFile(documentId: number, fileName: string): void {
    const fileExtension = CommonUtils.getExtension(fileName);
    this.getFileBy(documentId, fileExtension).subscribe(
      response => {
        this.docService.download(response, fileName);
      },
      error => {
        this.translateService
          .get('SHARED.VIEW-FILE-ERROR')
          .toPromise()
          .then(fileNotAccessible => {
            this.notificationService.warning(fileNotAccessible);
          });
      },
    );
  }

  private getOrderDocumentTypeExternalName(): Observable<
    OrderDocumentTypeExternal[]
  > {
    const headers = this.authService.getBearerRequestHeaders();
    return this.httpClient.get<OrderDocumentTypeExternal[]>(
      `${environment.orderDocumentServiceUrl}/externalDocumentTypeNames`,
      { headers },
    );
  }

  private resolveOrderDocumentTypeExternalName(
    orderDocType: OrderDocumentType,
  ): string {
    if (
      !this.allDocumentTypesExternal ||
      this.allDocumentTypesExternal.length === 0
    ) {
      this.resolveCmrOrderDocumentTypeExternalForBusinessUnit(orderDocType);
      return orderDocType.name;
    }
    const external = this.allDocumentTypesExternal.find(
      e => e.orderDocumentTypeId === orderDocType.id,
    );
    const externalDocName = !!external
      ? external.externalName
      : orderDocType.name;
    this.resolveCmrOrderDocumentTypeExternalForBusinessUnit(orderDocType);
    return externalDocName;
  }

  private resolveCmrOrderDocumentTypeExternalForBusinessUnit(
    orderDocType: OrderDocumentType,
  ): void {
    if (orderDocType.code === Constants.CMR_DOCUMENT_IDENTIFIER) {
      if (
        !!this.allDocumentTypesExternal &&
        this.allDocumentTypesExternal.length > 0
      ) {
        this.cmrExternalNameForBusinessUnit =
          this.allDocumentTypesExternal.find(
            ex => ex.orderDocumentTypeId === orderDocType.id,
          );
      } else {
        this.cmrExternalNameForBusinessUnit = new OrderDocumentTypeExternal();
        this.cmrExternalNameForBusinessUnit.id = 0;
        this.cmrExternalNameForBusinessUnit.externalName = orderDocType.name;
        this.cmrExternalNameForBusinessUnit.orderDocumentTypeId =
          orderDocType.id;
      }
    }
  }

  getExternalDocumentTypeBy(orderDocType: OrderDocumentType) {
    if (
      !!this.allDocumentTypesExternal &&
      this.allDocumentTypesExternal.length > 0
    ) {
      const externalNameForBusinessUnit = this.allDocumentTypesExternal.find(
        ex => ex.orderDocumentTypeId === orderDocType.id,
      );
      if (!!externalNameForBusinessUnit) {
        return externalNameForBusinessUnit.externalName;
      }
    }
    return orderDocType.name;
  }

  getCmrOrderDocument(): OrderDocumentType {
    if (!!this.cmrExternalNameForBusinessUnit) {
      return this.allDocumentTypes.find(
        type =>
          type.externalNameForbusinessUnit ===
          this.cmrExternalNameForBusinessUnit.externalName,
      );
    }
  }
}
